import apiClient from './api';
import { TradeSet, TradeSetListResponse, TradeSetPresetLink, TradeSetCreateRequest } from '../types/preset';

// Simplified API response interface that matches the type defined in preset.ts
interface TradeSetExecutionResponse {
  success: boolean;
  message: string;
  order_id?: string;  // Updated to match snake_case convention
}

// Define the structure for the update payload
interface TradeSetUpdatePayload {
  name?: string;
  description?: string | null;
  webhook_enabled?: boolean;
  webhook_id?: string | null;
  preset_links?: TradeSetPresetLink[]; // Array of linked presets with order
}

// Define the structure specifically for updating preset links
interface PresetLinksUpdatePayload {
  preset_links: TradeSetPresetLink[];
}

// TradeSet service
export const tradeSetService = {
  // Get all tradeSets with pagination and optional symbol filtering
  async getTradeSets(page: number = 1, limit: number = 10, symbol?: string): Promise<TradeSetListResponse> {
    try {
      // Convert page-based to skip-based pagination for backend
      const skip = (page - 1) * limit;
      const params: any = { skip, limit };
      if (symbol) params.symbol = symbol;
      
      const response = await apiClient.get<TradeSetListResponse>(`/tradeset`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching tradeSets:', error);
      throw error;
    }
  },

  // Get a single tradeSet by ID
  async getTradeSet(id: string): Promise<TradeSet> {
    try {
      const response = await apiClient.get<TradeSet>(`/tradeset/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tradeSet ${id}:`, error);
      throw error;
    }
  },

  // Create a new tradeSet
  async createTradeSet(tradeSetData: TradeSetCreateRequest): Promise<TradeSet> {
    try {
      const response = await apiClient.post<TradeSet>(`/tradeset`, tradeSetData);
      return response.data;
    } catch (error) {
      console.error('Error creating tradeSet:', error);
      throw error;
    }
  },

  // Update an existing tradeSet
  async updateTradeSet(id: string, tradeSetData: TradeSetUpdatePayload): Promise<TradeSet> {
    try {
      const response = await apiClient.put<TradeSet>(`/tradeset/${id}`, tradeSetData);
      return response.data;
    } catch (error) {
      console.error(`Error updating tradeSet ${id}:`, error);
      throw error;
    }
  },

  // Update just the preset links for a tradeSet (using PATCH endpoint)
  async updateTradeSetPresets(id: string, presetLinks: TradeSetPresetLink[]): Promise<TradeSet> {
    try {
      const payload: PresetLinksUpdatePayload = { preset_links: presetLinks };
      const response = await apiClient.patch<TradeSet>(`/tradeset/${id}/presets`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error updating preset links for tradeSet ${id}:`, error);
      throw error;
    }
  },

  // Delete a tradeSet
  async deleteTradeSet(id: string): Promise<boolean> {
    try {
      await apiClient.delete(`/tradeset/${id}`);
      return true;
    } catch (error) {
      console.error(`Error deleting tradeSet ${id}:`, error);
      throw error;
    }
  },

  // Execute a tradeSet
  async executeTradeSet(id: string): Promise<TradeSetExecutionResponse> {
    try {
      const response = await apiClient.post<TradeSetExecutionResponse>(`/tradeset/${id}/execute`);
      return response.data;
    } catch (error) {
      console.error(`Error executing tradeSet ${id}:`, error);
      throw error;
    }
  }
};

export default tradeSetService; 